import { useState, useMemo, useEffect, useRef } from "react";
import axios from "axios";
import OnboardingShell from "./components/OnboardingShell";
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  Check,
  User,
  Briefcase,
  ShieldCheck,
  KeyRound,
  FileSignature,
  FileText,
  Trash2,
  Users,
  Factory,
  ChevronDown
} from "lucide-react";
import { API_BASE_URL } from "./config";
import { WORLD_COUNTRIES, STATES_BY_COUNTRY, CITIES_BY_STATE } from "./data/worldData";

const COMPANY_SIZE_OPTIONS = [
  "1 - 10 employees",
  "11 - 50 employees",
  "51 - 200 employees",
  "201 - 500 employees",
  "501 - 1000 employees",
  "1000+ employees"
];

const INDUSTRY_TYPE_OPTIONS = [
  "Information Technology & Software",
  "Financial Services & Banking",
  "Healthcare & Life Sciences",
  "Retail & E-commerce",
  "Manufacturing & Industrial",
  "Real Estate & Construction",
  "Education & EdTech",
  "Logistics & Supply Chain",
  "Media & Entertainment",
  "Professional Services & Consulting",
  "Other / Services"
];

// Reusable Custom Select Dropdown that ALWAYS opens downwards below the field
function SelectDropdownField({ name, label, icon: Icon, placeholder, value, options = [], onChange, error, isRequired = true }) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isValid = (value || "").toString().trim().length > 0 && !error;

  return (
    <div className="field-group" ref={wrapperRef} style={{ marginBottom: "16px", position: "relative" }}>
      <label className="field-label" htmlFor={name} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <span>{label}</span>
        {isRequired && <span style={{ color: "#ef4444", fontWeight: "bold" }}>*</span>}
      </label>

      <div
        className={`input-wrapper ${error ? "has-error" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: "pointer", position: "relative" }}
      >
        <div className="input-icon-inside" style={{ left: "12px" }}>
          <Icon size={18} />
        </div>

        <div
          className="onboard-input"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingLeft: "38px",
            paddingRight: isValid ? "54px" : "30px",
            color: value ? "#0f172a" : "#94a3b8",
            fontSize: "13.5px",
            userSelect: "none",
            fontWeight: value ? 500 : 400
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {value || placeholder}
          </span>
        </div>

        {/* Right side icons container: Green Checkmark + Chevron Arrow */}
        <div style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", display: "flex", alignItems: "center", gap: "4px", pointerEvents: "none" }}>
          {isValid && <Check size={16} style={{ color: "#10b981" }} />}
          <ChevronDown
            size={16}
            style={{
              color: "#64748b",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s"
            }}
          />
        </div>
      </div>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 9999,
            marginTop: "4px",
            maxHeight: "220px",
            overflowY: "auto",
            background: "#ffffff",
            border: "1px solid #cbd5e1",
            borderRadius: "8px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            padding: "4px"
          }}
        >
          {options.map((opt, idx) => (
            <div
              key={idx}
              onClick={() => {
                onChange(name, opt);
                setIsOpen(false);
              }}
              style={{
                padding: "9px 12px",
                fontSize: "13px",
                color: "#1e293b",
                cursor: "pointer",
                borderRadius: "6px",
                transition: "background 0.15s",
                fontWeight: opt === value ? 600 : 400,
                background: opt === value ? "#eef2ff" : "transparent"
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = opt === value ? "#e0e7ff" : "#f1f5f9")}
              onMouseLeave={(e) => (e.currentTarget.style.background = opt === value ? "#eef2ff" : "transparent")}
            >
              {opt}
            </div>
          ))}
        </div>
      )}

      {error && <p className="onboard-error" style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>{error}</p>}
    </div>
  );
}

// Reusable Autocomplete field component for Country, State, and City
function AutocompleteField({ name, label, icon: Icon, placeholder, value, options = [], onChange, error, isRequired = true }) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  const filteredOptions = useMemo(() => {
    const query = (value || "").toString().trim().toLowerCase();
    if (!query) return options.slice(0, 50);
    return options.filter((opt) => opt.toLowerCase().includes(query)).slice(0, 50);
  }, [value, options]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isValid = (value || "").toString().trim().length > 0 && !error;

  return (
    <div className="field-group" ref={wrapperRef} style={{ marginBottom: "16px", position: "relative" }}>
      <label className="field-label" htmlFor={name} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <span>{label}</span>
        {isRequired && <span style={{ color: "#ef4444", fontWeight: "bold" }}>*</span>}
      </label>

      <div className={`input-wrapper ${error ? "has-error" : ""}`} style={{ position: "relative" }}>
        <div className="input-icon-inside" style={{ left: "12px" }}>
          <Icon size={18} />
        </div>
        <input
          id={name}
          type="text"
          autoComplete="off"
          className="onboard-input"
          name={name}
          placeholder={placeholder}
          value={value || ""}
          style={{
            paddingLeft: "38px",
            paddingRight: isValid ? "34px" : "14px",
            fontSize: "13.5px",
            color: "#0f172a",
            fontWeight: 500
          }}
          onFocus={() => setIsOpen(true)}
          onChange={(e) => {
            onChange(name, e.target.value);
            setIsOpen(true);
          }}
        />
        {isValid && (
          <Check
            size={16}
            style={{
              color: "#10b981",
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none"
            }}
          />
        )}
      </div>

      {isOpen && filteredOptions.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 9999,
            marginTop: "4px",
            maxHeight: "180px",
            overflowY: "auto",
            background: "#ffffff",
            border: "1px solid #cbd5e1",
            borderRadius: "8px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
            padding: "4px"
          }}
        >
          {filteredOptions.map((opt, idx) => (
            <div
              key={idx}
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(name, opt);
                setIsOpen(false);
              }}
              style={{
                padding: "8px 12px",
                fontSize: "13px",
                color: "#1e293b",
                cursor: "pointer",
                borderRadius: "6px",
                transition: "background 0.15s",
                fontWeight: opt.toLowerCase() === (value || "").toLowerCase() ? 600 : 400,
                background: opt.toLowerCase() === (value || "").toLowerCase() ? "#f1f5f9" : "transparent"
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#e2e8f0")}
              onMouseLeave={(e) =>
                (e.currentTarget.style.background =
                  opt.toLowerCase() === (value || "").toLowerCase() ? "#f1f5f9" : "transparent")
              }
            >
              {opt}
            </div>
          ))}
        </div>
      )}

      {error && <p className="onboard-error" style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>{error}</p>}
    </div>
  );
}

function Step1CompanyInfo({ onNext }) {
  const getInitialForm = () => {
    return {
      companyName: "",
      purchaserName: "",
      purchaserDesignation: "",
      companySize: "",
      industryType: "",
      companyEmail: "",
      phone: "",
      addressLine1: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
      gstin: "",
      signature: "",
      website: "",
      emailVerified: false
    };
  };

  useEffect(() => {
    // Clear old saved draft data on mount so page opens completely fresh every time
    localStorage.removeItem("step1Data");
  }, []);

  const [form, setForm] = useState(getInitialForm);
  const [errors, setErrors] = useState({});
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpMessage, setOtpMessage] = useState("");
  const [otpError, setOtpError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const validateField = (name, value) => {
    const val = (value || "").toString();
    if (name === "website" || name === "gstin") return "";
    if (name === "signature") {
      if (!val) return "E-Signature is required";
      return "";
    }
    if (!val.trim()) return "Required";
    if (name === "companyEmail" && val.trim() && !/\S+@\S+\.\S+/.test(val)) return "Invalid email address";
    if (name === "pincode" && val.trim() && !/^[a-zA-Z0-9\s\-]{3,10}$/.test(val.trim())) return "Invalid pincode";
    return "";
  };

  const syncFormState = (updated) => {
    const fullAddress = [updated.addressLine1, updated.city, updated.state, updated.country, updated.pincode]
      .filter(Boolean)
      .join(", ");
    const finalData = { ...updated, address: fullAddress };
    setForm(finalData);
    localStorage.setItem("step1Data", JSON.stringify(finalData));
    return finalData;
  };

  const handleValueChange = (name, value) => {
    const updated = { ...form, [name]: value };
    if (name === "companyEmail" && value !== form.companyEmail) {
      updated.emailVerified = false;
      setOtpSent(false);
      setOtpMessage("");
    }
    syncFormState(updated);
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    handleValueChange(name, value);
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setErrors((prev) => ({ ...prev, signature: "Please upload an image file (PNG, JPG, SVG)" }));
        return;
      }
      const reader = new FileReader();
      reader.onload = (uploadEvent) => {
        const dataUrl = uploadEvent.target.result;
        const updated = { ...form, signature: dataUrl };
        syncFormState(updated);
        setErrors((prev) => ({ ...prev, signature: "" }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveSignature = () => {
    const updated = { ...form, signature: "" };
    syncFormState(updated);
    setErrors((prev) => ({ ...prev, signature: "E-Signature is required" }));
  };

  const handleSendOtp = async () => {
    if (!form.companyEmail || errors.companyEmail) {
      setErrors((prev) => ({ ...prev, companyEmail: "Please enter a valid email address first" }));
      return;
    }

    setIsSending(true);
    setOtpMessage("");
    setOtpError("");

    try {
      const res = await axios.post(`${API_BASE_URL}/api/otp/send`, { email: form.companyEmail });
      setOtpSent(true);
      setOtpMessage(res.data?.message || "OTP code sent to your email!");
    } catch (err) {
      setOtpError(err.response?.data?.error || err.message || "Failed to send OTP. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setOtpError("Please enter the 6-digit OTP code");
      return;
    }

    setIsVerifying(true);
    setOtpError("");
    setOtpMessage("");

    try {
      await axios.post(`${API_BASE_URL}/api/otp/verify`, { email: form.companyEmail, otp: otpCode });
      const updated = { ...form, emailVerified: true };
      syncFormState(updated);
      setOtpMessage("Email verified successfully! ✓");
      setOtpSent(false);
    } catch (err) {
      setOtpError(err.response?.data?.error || err.message || "Invalid OTP code. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const validateAll = () => {
    const fieldsToValidate = [
      "companyName",
      "purchaserName",
      "purchaserDesignation",
      "companySize",
      "industryType",
      "companyEmail",
      "phone",
      "addressLine1",
      "city",
      "state",
      "country",
      "pincode",
      "signature"
    ];
    const newErrors = {};
    fieldsToValidate.forEach((k) => {
      const err = validateField(k, form[k]);
      if (err) newErrors[k] = err;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    const isValid = validateAll();
    if (!isValid) {
      return;
    }
    if (!form.companyEmail) {
      setErrors((prev) => ({ ...prev, companyEmail: "Company Email is required" }));
      return;
    }
    if (!form.signature) {
      setErrors((prev) => ({ ...prev, signature: "E-Signature is required" }));
      return;
    }
    if (!form.emailVerified) {
      setOtpError("Please verify your email address via OTP before continuing.");
      return;
    }

    const finalForm = syncFormState(form);
    localStorage.setItem("step1Data", JSON.stringify(finalForm));
    if (onNext) onNext();
  };

  // State Options based on selected Country
  const stateOptions = useMemo(() => {
    const selectedCountry = (form.country || "").trim();
    if (selectedCountry && STATES_BY_COUNTRY[selectedCountry]) {
      return STATES_BY_COUNTRY[selectedCountry];
    }
    return Array.from(new Set(Object.values(STATES_BY_COUNTRY).flat()));
  }, [form.country]);

  // City Options based on selected State
  const cityOptions = useMemo(() => {
    const selectedState = (form.state || "").trim();
    if (selectedState && CITIES_BY_STATE[selectedState]) {
      return CITIES_BY_STATE[selectedState];
    }
    return Array.from(new Set(Object.values(CITIES_BY_STATE).flat()));
  }, [form.state]);

  const renderField = (name, label, icon, placeholder, isRequired = true, type = "text") => {
    const Icon = icon;
    const val = (form[name] || "").toString();
    const isValid = val.trim().length > 0 && !errors[name];

    return (
      <div className="field-group" style={{ marginBottom: "16px" }}>
        <label className="field-label" htmlFor={name} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <span>{label}</span>
          {isRequired && <span style={{ color: "#ef4444", fontWeight: "bold" }}>*</span>}
        </label>
        <div className={`input-wrapper ${errors[name] ? "has-error" : ""}`}>
          <div className="input-icon-inside" style={{ left: "12px" }}>
            <Icon size={17} />
          </div>
          <input
            id={name}
            type={type}
            className="onboard-input"
            name={name}
            placeholder={placeholder}
            value={form[name] || ""}
            style={{
              paddingLeft: "38px",
              paddingRight: isValid ? "34px" : "14px",
              fontSize: "13.5px",
              color: "#0f172a",
              fontWeight: 500
            }}
            onChange={handleChange}
          />
          {isValid && (
            <Check
              size={16}
              style={{
                color: "#10b981",
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none"
              }}
            />
          )}
        </div>
        {errors[name] && <p className="onboard-error" style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>{errors[name]}</p>}
      </div>
    );
  };

  return (
    <OnboardingShell step={1}>
      <div className="form-header-row">
        <div className="form-header-content">
          <div className="onboard-eyebrow">STEP 1 OF 5</div>
          <h2 className="onboard-title">Company information</h2>
          <p className="onboard-subtext">Tell us about your company to start onboarding onto VRM AI TECHNOLOGY.</p>
        </div>
      </div>

      <div className="form-grid">
        {/* LEFT COLUMN: COMPANY & CONTACT DETAILS */}
        <div className="form-col-left">
          {renderField("companyName", "Company Name", Building2, "Test Company", true)}
          
          <SelectDropdownField
            name="industryType"
            label="Industry Type"
            icon={Factory}
            placeholder="Select Industry Type"
            value={form.industryType}
            options={INDUSTRY_TYPE_OPTIONS}
            onChange={handleValueChange}
            error={errors.industryType}
            isRequired={true}
          />

          <SelectDropdownField
            name="companySize"
            label="Company Size"
            icon={Users}
            placeholder="Select Company Size"
            value={form.companySize}
            options={COMPANY_SIZE_OPTIONS}
            onChange={handleValueChange}
            error={errors.companySize}
            isRequired={true}
          />

          {renderField("purchaserName", "Purchaser Name", User, "John Doe", true)}
          {renderField("purchaserDesignation", "Purchaser Designation", Briefcase, "CEO / Director", true)}

          {/* EMAIL & OTP VERIFICATION */}
          <div>
            {renderField("companyEmail", "Company Email", Mail, "hello@acme.com", true)}

            <div style={{ marginTop: -8, marginBottom: 16 }}>
              {form.emailVerified ? (
                <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 12px", background: "#dcfce7", color: "#166534", borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
                  <ShieldCheck size={16} /> Email Verified
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={isSending || !form.companyEmail}
                  className="admin-btn-outline"
                  style={{ fontSize: 12, padding: "6px 14px", cursor: "pointer" }}
                >
                  {isSending ? "Sending OTP..." : otpSent ? "Resend OTP" : "Send Email OTP"}
                </button>
              )}
            </div>

            {/* OTP ENTER BOX */}
            {otpSent && !form.emailVerified && (
              <div style={{ marginTop: 8, marginBottom: 16, padding: 14, background: "#f8fafc", border: "1px solid #cbd5e1", borderRadius: 8 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#334155", display: "block", marginBottom: 6 }}>
                  Enter 6-digit OTP code sent to your email:
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="text"
                    maxLength={6}
                    placeholder="123456"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    style={{ width: 140, padding: "8px 12px", fontSize: 16, letterSpacing: 3, fontWeight: "bold", border: "1px solid #cbd5e1", borderRadius: 6 }}
                  />
                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={isVerifying}
                    className="onboard-button onboard-button--primary"
                    style={{ padding: "8px 16px", fontSize: 13 }}
                  >
                    {isVerifying ? "Verifying..." : "Verify OTP"}
                  </button>
                </div>
              </div>
            )}

            {otpMessage && <p style={{ color: "#10b981", fontSize: 13, marginTop: -8, marginBottom: 12 }}>{otpMessage}</p>}
            {otpError && <p style={{ color: "#ef4444", fontSize: 13, marginTop: -8, marginBottom: 12 }}>{otpError}</p>}
          </div>

          {renderField("phone", "Phone Number", Phone, "+91 98765 43210", true)}
        </div>

        {/* RIGHT COLUMN: ADDRESS (ORDER: Address Line 1 -> City -> State -> Country -> Pincode/GSTIN -> Website -> E-Signature) */}
        <div className="form-col-right">
          {renderField("addressLine1", "Address Line 1", MapPin, "37, Vallal 2nd Cross St", true)}

          <AutocompleteField
            name="city"
            label="City"
            icon={MapPin}
            placeholder="Type city (e.g. Chennai)"
            value={form.city}
            options={cityOptions}
            onChange={handleValueChange}
            error={errors.city}
            isRequired={true}
          />

          <AutocompleteField
            name="state"
            label="State"
            icon={MapPin}
            placeholder="Type state (e.g. Tamil Nadu)"
            value={form.state}
            options={stateOptions}
            onChange={handleValueChange}
            error={errors.state}
            isRequired={true}
          />

          <AutocompleteField
            name="country"
            label="Country"
            icon={Globe}
            placeholder="Type country (e.g. India)"
            value={form.country}
            options={WORLD_COUNTRIES}
            onChange={handleValueChange}
            error={errors.country}
            isRequired={true}
          />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            {renderField("pincode", "Pincode", KeyRound, "600116", true)}
            {renderField("gstin", "GSTIN / Tax ID (Optional)", FileText, "33AAAAA0000A1Z5", false)}
          </div>

          {renderField("website", "Website (Optional)", Globe, "https://acme.com", false)}

          {/* E-SIGNATURE UPLOAD FIELD */}
          <div className="field-group" style={{ marginBottom: "16px" }}>
            <label className="field-label" style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span>E-Signature</span>
              <span style={{ color: "#ef4444", fontWeight: "bold" }}>*</span>
            </label>

            {form.signature ? (
              <div
                style={{
                  border: "1px solid #818cf8",
                  borderRadius: "8px",
                  padding: "0 12px",
                  background: "#eef2ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                  height: "46px"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <img
                    src={form.signature}
                    alt="E-Signature Preview"
                    style={{
                      height: "30px",
                      maxWidth: "110px",
                      objectFit: "contain",
                      border: "1px solid #cbd5e1",
                      borderRadius: "4px",
                      background: "#ffffff",
                      padding: "2px"
                    }}
                  />
                  <span style={{ fontSize: "12px", color: "#15803d", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px" }}>
                    <Check size={14} /> Attached
                  </span>
                </div>

                <button
                  type="button"
                  onClick={handleRemoveSignature}
                  style={{
                    background: "#fee2e2",
                    color: "#dc2626",
                    border: "none",
                    borderRadius: "6px",
                    padding: "4px 10px",
                    fontSize: "11px",
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px"
                  }}
                >
                  <Trash2 size={12} /> Remove
                </button>
              </div>
            ) : (
              <div
                style={{
                  border: errors.signature ? "1.5px dashed #ef4444" : "1.5px dashed #818cf8",
                  borderRadius: "8px",
                  padding: "0 14px",
                  height: "46px",
                  background: errors.signature ? "#fef2f2" : "#f8fafc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s",
                  cursor: "pointer"
                }}
              >
                <label
                  htmlFor="signature-upload"
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#4f46e5",
                    width: "100%",
                    justifyContent: "center"
                  }}
                >
                  <FileSignature size={18} />
                  <span>Upload E-Signature (PNG/JPG)</span>
                </label>
                <input
                  id="signature-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleSignatureUpload}
                  style={{ display: "none" }}
                />
              </div>
            )}
            {errors.signature && <p className="onboard-error" style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}>{errors.signature}</p>}
          </div>
        </div>
      </div>

      <div className="button-row" style={{ justifyContent: "flex-end", marginTop: 16 }}>
        <button className="onboard-button onboard-button--primary" onClick={handleSubmit} type="button" style={{ minWidth: 200 }}>
          Continue <span style={{ marginLeft: 8 }}>→</span>
        </button>
      </div>
    </OnboardingShell>
  );
}

export default Step1CompanyInfo;